'use client';

import { handle_GTM_81 } from 'gtmEvent/81';
import { handle_GTM_69 } from 'gtmEvent/69';
import classNames from 'classnames';
import GloLayout from 'code-micro/common/GloLayoutA';
import { ContentServerProps } from 'code-micro/props/content';
import { useIntersectionObservers } from 'eco-design-micro/hooks/useIntersectionObservers';
import EcoImg from 'eco-design/base/eco-img';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/navigation';
import { normalizeKspSwiperData } from '../setting/config';
import { ItfKspSwiperData, ItfKspSwiperItem, ItfKspSwiperItemModal, ItfKspSwiperItemModalItem, ItfKspSwiperTab } from '../setting/type';
import { Plus } from './icons';
import './index.scss';
import Modal from './modal';
import { Swiper as SwiperType } from 'swiper/types';

interface ItfAniArrItem {
    name: string;
    cls: string;
}

/** 与配置面板 subitem-initIndex 一致：tabIndex * 100 + 卡片下标 */
const KSP_INIT_STRIDE = 100;
const decodeKspInit = (n: number) => ({
    tabIdx: Math.floor(n / KSP_INIT_STRIDE),
    cardIdx: n % KSP_INIT_STRIDE,
});

export default function Content({ id, data = {}, isEditStatus, initIndex = 0 }: ContentServerProps) {
    const norm = normalizeKspSwiperData(data as ItfKspSwiperData);
    const { theme, title, titleColor, tabs } = norm;
    const tabList: ItfKspSwiperTab[] = tabs?.length ? tabs : [];
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const prevInitRef = useRef<number | null>(null);
    const safeTabIdx = Math.min(activeTabIndex, Math.max(0, tabList.length - 1));
    const items = (tabList[safeTabIdx]?.items ?? []) as ItfKspSwiperItem[];
    const prefixCls = 'ksp-swiper-tabs';
    const [type, setType] = useState<'narrow' | 'wide' | ''>('wide'); // narrow: 4个及以上，wide: 4个以下
    const [gap, setGap] = useState<number>(10);
    const [modalData, setModalData] = useState<ItfKspSwiperItemModal & { id: string }>({
        id: '',
        name: '',
        text: '',
        items: [] as ItfKspSwiperItemModalItem[],
    });
    const [enter, setEnter] = useState({
        title: isEditStatus,
        tabs: isEditStatus,
        content: isEditStatus,
    });
    const [swiper, setSwiper] = useState<SwiperType | null>(null);
    const swiperRef = useRef<any>(null);

    // 新增：用于记录状态的ref
    const scrollLockRef = useRef({
        isLocked: false,
        scrollTop: 0,
        scrollbarWidth: 0,
        originalScrollBehavior: '',
        originalBodyPaddingRight: '',
        originalBodyOverflow: '',
    });

    // 新增：无感知滚动锁定函数
    const lockScroll = () => {
        if (scrollLockRef.current.isLocked) return;

        const st = window.scrollY || document.documentElement.scrollTop || window.pageYOffset;
        const html = document.documentElement;
        const body = document.body;

        // 记录状态
        scrollLockRef.current.scrollTop = st;
        scrollLockRef.current.originalScrollBehavior = html.style.scrollBehavior;
        scrollLockRef.current.originalBodyPaddingRight = body.style.paddingRight;
        scrollLockRef.current.originalBodyOverflow = body.style.overflow;

        // 计算滚动条宽度（防止布局跳动）
        scrollLockRef.current.scrollbarWidth = window.innerWidth - html.clientWidth;

        // 1. 禁用平滑滚动
        html.style.scrollBehavior = 'auto';

        // 2. 立即滚动到当前位置（确保位置准确）
        window.scrollTo(0, st);

        // 3. 使用requestAnimationFrame确保在下一帧执行锁定
        requestAnimationFrame(() => {
            // 4. 设置锁定样式
            body.style.overflow = 'hidden';
            body.style.position = 'fixed';
            body.style.top = `-${st}px`;
            body.style.left = '0';
            body.style.right = '0';
            body.style.width = '100%';

            // 5. 处理滚动条（防止布局偏移）
            if (scrollLockRef.current.scrollbarWidth > 0) {
                body.style.paddingRight = `${scrollLockRef.current.scrollbarWidth}px`;
            }

            // 6. 立即滚动到顶部（无动画）
            window.scrollTo({
                top: 0,
                // @ts-ignore
                behavior: 'instant', // 或 'auto'
            });

            scrollLockRef.current.isLocked = true;
        });

        return st;
    };

    // 新增：无感知滚动解锁函数
    const unlockScroll = () => {
        if (!scrollLockRef.current.isLocked) return;

        const html = document.documentElement;
        const body = document.body;
        const { scrollTop } = scrollLockRef.current;

        // 1. 恢复滚动行为
        html.style.scrollBehavior = scrollLockRef.current.originalScrollBehavior;

        // 2. 移除锁定样式
        body.style.overflow = scrollLockRef.current.originalBodyOverflow;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.paddingRight = scrollLockRef.current.originalBodyPaddingRight;

        // 3. 在下一帧恢复滚动位置（无感知）
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // 双重requestAnimationFrame确保DOM已更新
                window.scrollTo({
                    top: scrollTop,
                    // @ts-ignore
                    behavior: 'instant', // 或 'auto'
                });

                // 额外确保一次（防止某些浏览器问题）
                setTimeout(() => {
                    window.scrollTo({
                        top: scrollTop,
                        // @ts-ignore
                        behavior: 'instant',
                    });
                }, 16);

                scrollLockRef.current.isLocked = false;
            });
        });
    };

    const handleOpen = (item: ItfKspSwiperItem) => {
        if (!item?.isModal) return;
        const { name, text, items } = item.modal;

        // 锁定滚动（无感知）
        lockScroll();

        // 设置弹窗数据
        setModalData({
            id,
            name,
            text,
            items,
        });

        handle_GTM_81({
            title_name: name,
            button_name: 'Add Icon',
            __btn_id1: `editor-${prefixCls}-${id}`,
            __btn_id2: item.name,
            __btn_type: 'common',
        });
    };

    const handleClose = () => {
        // 解锁滚动（无感知）
        unlockScroll();

        // 关闭弹窗
        setModalData(
            {} as ItfKspSwiperItemModal & {
                id: string;
            },
        );
    };

    const arr: ItfAniArrItem[] = [
        { name: 'title', cls: `${prefixCls}-info-title` },
        { name: 'tabs', cls: `${prefixCls}-tabs` },
        { name: 'content', cls: `${prefixCls}-swiper-box` },
    ];

    const handleGTM = (btn: string) => {
        handle_GTM_69({
            button_name: btn,
            position: `Series_KspSwiper-${id}`,
            __btn_id1: `editor-${prefixCls}-${id}`,
            __btn_id2: btn,
            __btn_type: 'common',
        });
    };

    const { tabIdx: initTabFromTool, cardIdx: initCardFromTool } = decodeKspInit(initIndex);

    const swiperInitialSlide = initTabFromTool === safeTabIdx && items.length > 0 ? Math.min(initCardFromTool, items.length - 1) : 0;

    useIntersectionObservers(
        {
            selectors: [
                `.editor-${prefixCls}-${id} .${prefixCls}-info-title`,
                `.editor-${prefixCls}-${id} .${prefixCls}-tabs`,
                `.editor-${prefixCls}-${id} .${prefixCls}-swiper-box`,
            ],
            onEnter: (e: Element) => {
                const aim: ItfAniArrItem | undefined = arr?.find((i: ItfAniArrItem) => i.cls === e.classList?.[0]);
                if (aim) {
                    setEnter((prev) => ({ ...prev, [aim.name]: true }));
                }
            },
            // onLeave: (e: Element) => {
            //     const aim: ItfAniArrItem | undefined = arr?.find((i: ItfAniArrItem) => i.cls === e.classList?.[0]);
            //     if (aim) {
            //         setEnter((prev) => ({ ...prev, [aim.name]: false }));
            //     }
            // },
        },
        [],
        isEditStatus,
    );

    // 新增：处理浏览器前进后退按钮
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handlePopState = () => {
            if (scrollLockRef.current.isLocked) {
                unlockScroll();
                setModalData({} as any);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (type === 'wide') {
                setGap(window.innerWidth > 1024 ? 12 : 10);
            } else if (type === 'narrow') {
                setGap(window.innerWidth > 1440 ? 12 : 10);
            }
        }
    }, []);

    useEffect(() => {
        setActiveTabIndex((t) => Math.min(t, Math.max(0, tabList.length - 1)));
    }, [tabList.length]);

    useEffect(() => {
        if (prevInitRef.current === null) {
            prevInitRef.current = initIndex;
            const { tabIdx } = decodeKspInit(initIndex);
            const safeTab = Math.min(Math.max(0, tabIdx), Math.max(0, tabList.length - 1));
            setActiveTabIndex(safeTab);
            return;
        }
        if (initIndex !== prevInitRef.current) {
            prevInitRef.current = initIndex;
            const { tabIdx } = decodeKspInit(initIndex);
            const safeTab = Math.min(Math.max(0, tabIdx), Math.max(0, tabList.length - 1));
            setActiveTabIndex(safeTab);
        }
    }, [initIndex, tabList.length]);

    useEffect(() => {
        setType(items?.length >= 4 ? 'narrow' : items?.length >= 1 ? 'wide' : '');
    }, [items]);

    // 新增：组件卸载时清理
    useEffect(() => {
        return () => {
            if (scrollLockRef.current.isLocked) {
                unlockScroll();
            }
        };
    }, []);

    useEffect(() => {
        if (swiperRef.current && swiper) {
            const prev = swiperRef.current.querySelector('.swiper-button-prev');
            const next = swiperRef.current.querySelector('.swiper-button-next');
            if (prev) {
                prev.classList.add('GtmModuleData-btn-common');
                prev.setAttribute('data-btn-id', '<');
                prev.addEventListener('click', () => handleGTM('<'));
            }
            if (next) {
                next.classList.add('GtmModuleData-btn-common');
                next.setAttribute('data-btn-id', '>');
                next.addEventListener('click', () => handleGTM('>'));
            }
            return () => {
                if (prev) {
                    prev.removeEventListener('click', () => handleGTM('<'));
                }
                if (next) {
                    next.removeEventListener('click', () => handleGTM('>'));
                }
            };
        }
    }, [swiperRef.current, swiper]);

    return (
        <GloLayout className={`editor-${prefixCls} editor-${prefixCls}-${id} theme-${theme} type-${type} `} id={id} data={data} floorName={prefixCls}>
            <div className={`${prefixCls}-info`}>
                {title && (
                    <div className={`${prefixCls}-info-title ${enter?.title ? 'enter' : ''}`} style={{ color: titleColor }} title={title}>
                        {title}
                    </div>
                )}
            </div>
            {tabList.length > 1 && (
                <div className={`${prefixCls}-tabs`} role="tablist">
                    {tabList.map((tab: ItfKspSwiperTab, ti: number) => {
                        const label = tab.name?.trim();
                        const isActive = ti === safeTabIdx;
                        return (
                            <button
                                key={`tab-${ti}`}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={classNames(`${prefixCls}-tab`, { active: isActive, enter: enter?.tabs })}
                                style={
                                    {
                                        '--ksp-tab-enter-delay': enter?.tabs ? `${(ti + 1) * 0.12}s` : '0s',
                                    } as CSSProperties
                                }
                                onClick={() => {
                                    setActiveTabIndex(ti);
                                    handleGTM(label);
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
            <div className={`${prefixCls}-swiper-box`} ref={swiperRef}>
                <Swiper
                    key={`${type}-${safeTabIdx}-${initIndex}-${items.length}`}
                    className={`${prefixCls}-swiper`}
                    initialSlide={swiperInitialSlide}
                    spaceBetween={gap}
                    slidesPerView={'auto'}
                    modules={[Navigation]}
                    navigation={true}
                    onSwiper={(swiperIns: SwiperType) => setSwiper(swiperIns)}
                >
                    {items?.map((item: ItfKspSwiperItem, index: number) => {
                        const { name, nameColor, text, textColor, pc, h5, isModal } = item;
                        const itemClasses = classNames({
                            [`${prefixCls}-swiper-item`]: true,
                            [`${prefixCls}-swiper-item-${index}`]: true,
                            [`clickable`]: isModal,
                            ['enter']: enter?.content,
                        });
                        return (
                            <SwiperSlide
                                className={itemClasses}
                                key={`${safeTabIdx}-${index}`}
                                style={{ transitionDelay: enter?.content ? `${(index + 1) * 0.15}s` : '0s' }}
                            >
                                <div
                                    className={`${prefixCls}-swiper-item-inner GtmModaleData-btn-common`}
                                    data-btn-id={item.name}
                                    onClick={() => handleOpen(item)}
                                >
                                    <div className={`${prefixCls}-swiper-item-img`}>
                                        <EcoImg pc={pc} h5={h5} />
                                    </div>
                                    <div className={`${prefixCls}-swiper-item-content`}>
                                        {name && (
                                            <div className={`${prefixCls}-swiper-item-content-name`} style={{ color: nameColor || '' }} title={name}>
                                                {name}
                                            </div>
                                        )}
                                        {text && (
                                            <div className={`${prefixCls}-swiper-item-content-text`} style={{ color: textColor || '' }} title={text}>
                                                {text}
                                            </div>
                                        )}
                                    </div>
                                    {isModal && (
                                        <button className={`${prefixCls}-swiper-item-btn GtmModuleData-btn-common`}>
                                            <div className={`${prefixCls}-swiper-item-btn-plus`}>{Plus}</div>
                                            <div className="circle circle-1"></div>
                                            <div className="circle circle-2"></div>
                                        </button>
                                    )}
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
                {modalData?.id && <Modal theme={theme} data={modalData} callback={handleClose} />}
            </div>
            {/* 移除原有的全局样式，使用内联样式控制 */}
        </GloLayout>
    );
}
